import { z } from 'zod/v4';
import { CAPA_ACTION_STATUS } from '../../constants.js';

const capaActionStatusValues = Object.values(CAPA_ACTION_STATUS) as [
  string,
  ...string[],
];

// Attachment response schema - matches file data from FILES table.
// Strict: an undeclared key here is a serialization error, not a silent strip.
export const ActionAttachmentSchema = z
  .object({
    id: z.string(),
    url: z.string().nullable(),
    bucketName: z.string(),
    isPrivate: z.number().nullable(),
    name: z.string().nullable(),
    contentType: z.string().nullable(),
    fileSize: z.number().nullable(),
    createdAt: z.union([z.string(), z.date()]).nullable(),
    createdByUserId: z.string().nullable(),
    previewUrl: z.string().nullable().optional(),
  })
  .strict();

// Person schema shared by assignees, reporters, watchers
export const ActionPersonSchema = z.object({
  id: z.string(),
  name: z.string(),
  firstName: z.string(),
  lastName: z.string().nullable(),
  companyId: z.string().nullable(),
  avatarFileUrl: z.string().nullable(),
});

// Source object schemas
const sourceAuditSchema = z.object({
  id: z.string(),
  name: z.string(),
  auditTypeId: z.string().nullable(),
  auditTypeName: z.string().nullable(),
  referenceNumber: z.string().nullable(),
  auditDate: z.date().nullable(),
  facilityScore: z.string().nullable(),
});

const sourceSlcpSchema = z.object({
  id: z.string(),
  objectId: z.string(),
  version: z.string(),
  status: z.string(),
  revision: z.string(),
  submittedOn: z.union([z.string(), z.date()]).nullable(),
});

const sourceObjectSchema = z.union([
  sourceAuditSchema,
  sourceSlcpSchema,
  z.object({}),
]);

// Remediation request file schema
export const RemediationRequestFileSchema = z.object({
  id: z.string(),
  url: z.string().nullable(),
  bucketName: z.string(),
  isPrivate: z.number().nullable(),
  name: z.string().nullable(),
  contentType: z.string().nullable(),
  fileSize: z.number().nullable(),
  createdAt: z.union([z.string(), z.date()]).nullable(),
  previewUrl: z.string().nullable().optional(),
});

// Remediation request schema
export const RemediationRequestSchema = z.object({
  id: z.string(),
  actionId: z.string(),
  status: z.string(),
  message: z.string(),
  attachmentFileIds: z.array(z.string()),
  requestedChangeMessage: z.string().optional().nullable(),
  requestedChangeDate: z.union([z.string(), z.date()]).optional().nullable(),
  requestedChangeUserId: z.string().optional().nullable(),
  createdAt: z.union([z.string(), z.date()]),
  /** Null on system-created requests (the CAPA v1 normalisation migrations). */
  createdByUserId: z.string().nullable(),
  createdByCompanyId: z.string(),
  updatedAt: z.union([z.string(), z.date()]),
  updatedByUserId: z.string().optional().nullable(),
  files: z.array(RemediationRequestFileSchema),
});

/**
 * One action linked to this one. A thin projection — the linked-actions query
 * selects these columns only, so a field the row card wants that is not here
 * (a due date, say) needs the SQL changed, not just the schema.
 */
export const ActionLinkedActionSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** CA / PA — drives the corrective vs preventative icon on the linked row. */
  type: z.string().nullable(),
  status: z.string(),
  description: z.string().nullable(),
  sourceType: z.string().nullable(),
  sourceId: z.string().nullable(),
  ownerCompanyId: z.string().nullable(),
  ownerCompanyName: z.string().nullable(),
  createdAt: z.union([z.string(), z.date()]).nullable(),
});

export type ActionLinkedAction = z.infer<typeof ActionLinkedActionSchema>;

// Parent CAP schema
const parentCapSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.string().nullable(),
  priority: z.string().nullable(),
  dueDate: z.date().nullable(),
});

// Tag schema
const tagSchema = z.object({
  id: z.string(),
  title: z.string(),
  colorCode: z.string(),
});

// The full ActionResource schema
export const ActionResourceSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  type: z.string().nullable(),
  impactAreaId: z.string().nullable(),
  sourceType: z.string().nullable(),
  sourceId: z.string().nullable(),
  priority: z.string().nullable(),
  status: z.enum(capaActionStatusValues).nullable(),
  dueDate: z.date().nullable(),
  createdAt: z.date().nullable(),
  updatedAt: z.date().nullable(),
  auditor: z.string().nullable(),
  finding: z.string().nullable(),
  goals: z.string().nullable(),
  requirement: z.string().nullable(),
  managementResponse: z.string().nullable(),
  recommendation: z.string().nullable(),
  rootCause: z.string().nullable(),
  isArchived: z.number(),
  startedAt: z.date().nullable(),
  endedAt: z.date().nullable(),
  attachments: z.array(ActionAttachmentSchema).nullable(),
  createdByUserId: z.string().nullable(),
  createdByCompanyId: z.string().nullable(),
  createdByCompanyName: z.string().nullable().optional(),
  auditFindingId: z.string().nullable(),
  assignees: z.array(ActionPersonSchema),
  reporters: z.array(ActionPersonSchema),
  ownerCompanyId: z.string().nullable(),
  ownerCompanyName: z.string().nullable(),
  internalCompanyCode: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  generatedBy: z.string().nullable().optional(),
  owner: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
      facilityProcesses: z.string().nullable(),
      country: z.string().nullable(),
      countryName: z.string().nullable(),
    })
    .optional(),
  source: sourceObjectSchema.optional(),
  tags: z.array(tagSchema).optional(),
  cap: parentCapSchema.nullable().optional(),
  watchers: z.array(ActionPersonSchema),
  users: z
    .array(
      z.object({
        userId: z.string(),
        type: z.string(),
        companyId: z.string().nullable(),
      }),
    )
    .optional(),
  companies: z
    .array(
      z.object({
        companyId: z.string(),
        type: z.string(),
        name: z.string().nullable(),
        officialName: z.string().nullable(),
        country: z.string().nullable(),
        countryName: z.string().nullable(),
        facilityProcesses: z.string().nullable(),
      }),
    )
    .optional(),
  remediationRequest: RemediationRequestSchema.nullable().optional(),
  linkedActions: z.array(ActionLinkedActionSchema).optional(),
});

export type ActionResource = z.infer<typeof ActionResourceSchema>;
